import React, { SetStateAction, useState } from "react";
import { Button, Form, Modal, Table } from "react-bootstrap";
import AddRoleModal from "./AddRoleModal";
import { useGeneralApiCall } from "@/services/useGeneralApiCall";

export type Role = {
  role_id: string;
  name: string;
  description: string;
  employee_count: number;
};

type RoleModalProps = {
  show: boolean;
  onHide: () => void;
  roles: Role[];
  setRoleModalShow: React.Dispatch<React.SetStateAction<boolean>>;
  setRoles: React.Dispatch<React.SetStateAction<Role[]>>;
};

const RoleModal: React.FC<RoleModalProps> = ({
  show,
  onHide,
  roles,
  setRoleModalShow,
  setRoles,
}) => {
  const [addRoleModalShow, setAddRoleModalShow] = useState(false);
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const [editedRole, setEditedRole] = useState<Partial<Role> | null>(null);
  const { putApi, getApi, deleteApi } = useGeneralApiCall();

  const handleEdit = (role: Role) => {
    setEditingRoleId(role.role_id);
    setEditedRole({ ...role });
  };

  const handleSave = () => {
    if (editingRoleId && editedRole) {
      // API call to save the edited role
      putApi(`/api/auth/v1/roles`, editingRoleId, editedRole).then(() => {
        getApi<SetStateAction<Role[]>>("/api/auth/v1/roles").then((res) => {
          setRoles(res.data);
        });
      });

      setEditingRoleId(null);
      setEditedRole(null);
    }
  };

  const handleDelete = (role_id: string) => {
    if (role_id) {
      deleteApi(`/api/auth/v1/roles`, role_id).then(() => {
        getApi<SetStateAction<Role[]>>("/api/auth/v1/roles").then((res) => {
          setRoles(res.data);
        });
      });
    }
  };

  const handleInputChange = (field: keyof Role, value: string) => {
    setEditedRole((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <>
      <Modal show={show} onHide={onHide} centered size="lg" backdrop="static"
        keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>Manage Roles</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2>Roles List</h2>
            <Button
              variant="success"
              onClick={() => {
                setAddRoleModalShow(true);
                setRoleModalShow(false);
              }}
            >
              Add Role
            </Button>
          </div>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Description</th>
                <th>Employee Count</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((role, index) => (
                <tr key={role.role_id}>
                  <td>{index + 1}</td>
                  <td>
                    {editingRoleId === role.role_id ? (
                      <Form.Control
                        type="text"
                        value={editedRole?.name || ""}
                        onChange={(e) =>
                          handleInputChange("name", e.target.value)
                        }
                      />
                    ) : (
                      role.name
                    )}
                  </td>
                  <td>
                    {editingRoleId === role.role_id ? (
                      <Form.Control
                        type="text"
                        value={editedRole?.description || ""} // Add description here
                        onChange={(e) =>
                          handleInputChange("description", e.target.value)
                        }
                      />
                    ) : (
                      role.description
                    )}
                  </td>
                  <td>{role.employee_count}</td>
                  <td>
                    {editingRoleId !== role.role_id && (
                      <Button
                        variant="warning"
                        size="sm"
                        className="me-2"
                        onClick={() => {
                          handleEdit(role);
                        }}
                      >
                        Edit
                      </Button>
                    )}
                    {editingRoleId === role.role_id && (
                      <Button
                        variant="warning"
                        size="sm"
                        className="me-2"
                        onClick={() => {
                          setEditingRoleId(null);
                          setEditedRole(null);
                        }}
                      >
                        Cancel
                      </Button>
                    )}
                    <Button
                      variant="danger"
                      size="sm"
                      className="me-2"
                      onClick={() => {
                        handleDelete(role.role_id);
                      }}
                    >
                      Delete
                    </Button>
                    {editingRoleId === role.role_id && (
                      <Button variant="success" size="sm" onClick={handleSave}>
                        Save
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
      </Modal>

      {/* AddRoleModal */}
      <AddRoleModal
        show={addRoleModalShow}
        onHide={() => {
          setAddRoleModalShow(false);
          setRoleModalShow(true);
        }}
        setRoles={setRoles}
      />
    </>
  );
};

export default RoleModal;
